import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Upload } from 'lucide-react';
import { services } from '../data/services';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const GetQuote = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    city: '',
    requirement: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email';
    if (!formData.service) newErrors.service = 'Please select a service';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.requirement.trim()) newErrors.requirement = 'Please describe your requirement';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      setIsSubmitted(true);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <main data-testid="quote-page" className="pt-24 md:pt-32 min-h-screen">
      <div className="container-wide">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-24 py-8 md:py-16">
          {/* Left Column - Info */}
          <div className="lg:col-span-5">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="eyebrow mb-6 block"
            >
              Get Quote
            </motion.span>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="text-display font-['DM_Sans'] mb-6"
            >
              Let's create something together
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-lg text-muted-foreground mb-8"
            >
              Share your requirements and we'll get back with ideas, design direction, and a quote.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="space-y-4"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <Check className="w-4 h-4 text-primary" />
                </div>
                <span className="text-sm">Quick response time</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <Check className="w-4 h-4 text-primary" />
                </div>
                <span className="text-sm">Transparent pricing</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <Check className="w-4 h-4 text-primary" />
                </div>
                <span className="text-sm">Custom solutions</span>
              </div>
            </motion.div>
          </div>

          {/* Right Column - Form */}
          <div className="lg:col-span-7">
            <AnimatePresence mode="wait">
              {isSubmitted ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  data-testid="form-success"
                  className="bg-secondary p-8 md:p-12 rounded-sm"
                >
                  <div className="text-center">
                    <div className="w-16 h-16 rounded-full bg-accent mx-auto mb-6 flex items-center justify-center">
                      <Check className="w-8 h-8 text-foreground" />
                    </div>
                    <h2 className="text-2xl md:text-3xl font-medium font-['DM_Sans'] mb-4">
                      Thanks for reaching out
                    </h2>
                    <p className="text-muted-foreground mb-8">
                      We'll get back with next steps.
                    </p>
                    <button
                      onClick={() => {
                        setIsSubmitted(false);
                        setFormData({
                          name: '',
                          phone: '',
                          email: '',
                          service: '',
                          city: '',
                          requirement: ''
                        });
                      }}
                      data-testid="submit-another"
                      className="btn-secondary"
                    >
                      Submit another request
                    </button>
                  </div>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  onSubmit={handleSubmit}
                  data-testid="quote-form"
                  className="space-y-8"
                >
                  {/* Row 1: Name & Phone */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                        Name *
                      </label>
                      <input
                        type="text"
                        data-testid="input-name"
                        value={formData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        placeholder="Your name"
                        className={`form-input ${errors.name ? 'border-destructive' : ''}`}
                      />
                      {errors.name && (
                        <span className="text-xs text-destructive mt-1">{errors.name}</span>
                      )}
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                        Phone *
                      </label>
                      <input
                        type="tel"
                        data-testid="input-phone"
                        value={formData.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                        placeholder="Phone number"
                        className={`form-input ${errors.phone ? 'border-destructive' : ''}`}
                      />
                      {errors.phone && (
                        <span className="text-xs text-destructive mt-1">{errors.phone}</span>
                      )}
                    </div>
                  </div>

                  {/* Row 2: Email & Service */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                        Email *
                      </label>
                      <input
                        type="email"
                        data-testid="input-email"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        placeholder="your@email.com"
                        className={`form-input ${errors.email ? 'border-destructive' : ''}`}
                      />
                      {errors.email && (
                        <span className="text-xs text-destructive mt-1">{errors.email}</span>
                      )}
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                        Service *
                      </label>
                      <Select
                        value={formData.service}
                        onValueChange={(value) => handleChange('service', value)}
                      >
                        <SelectTrigger
                          data-testid="select-service"
                          className={`form-input h-auto border-0 border-b px-0 ${errors.service ? 'border-destructive' : ''}`}
                        >
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent>
                          {services.map((service) => (
                            <SelectItem key={service.id} value={service.id}>
                              {service.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.service && (
                        <span className="text-xs text-destructive mt-1">{errors.service}</span>
                      )}
                    </div>
                  </div>

                  {/* Row 3: City */}
                  <div>
                    <label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                      City *
                    </label>
                    <input
                      type="text"
                      data-testid="input-city"
                      value={formData.city}
                      onChange={(e) => handleChange('city', e.target.value)}
                      placeholder="Your city"
                      className={`form-input ${errors.city ? 'border-destructive' : ''}`}
                    />
                    {errors.city && (
                      <span className="text-xs text-destructive mt-1">{errors.city}</span>
                    )}
                  </div>

                  {/* Row 4: Requirement */}
                  <div>
                    <label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                      Brief requirement *
                    </label>
                    <textarea
                      data-testid="input-requirement"
                      value={formData.requirement}
                      onChange={(e) => handleChange('requirement', e.target.value)}
                      placeholder="Describe your signage needs..."
                      rows={4}
                      className={`form-input resize-none ${errors.requirement ? 'border-destructive' : ''}`}
                    />
                    {errors.requirement && (
                      <span className="text-xs text-destructive mt-1">{errors.requirement}</span>
                    )}
                  </div>

                  {/* Row 5: File Upload (Placeholder) */}
                  <div>
                    <label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                      Upload reference (optional)
                    </label>
                    <div
                      data-testid="upload-placeholder"
                      className="border border-dashed border-border p-8 text-center rounded-sm bg-secondary/30 cursor-not-allowed opacity-60"
                    >
                      <Upload className="w-8 h-8 mx-auto mb-3 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">
                        File upload coming soon
                      </p>
                    </div>
                  </div>

                  {/* Submit */}
                  <div className="pt-4">
                    <button
                      type="submit"
                      data-testid="submit-quote"
                      className="btn-primary w-full md:w-auto"
                    >
                      Submit Request
                    </button>
                  </div>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </main>
  );
};

export default GetQuote;
